import { addDays, subDays } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { DEFAULT_DATE_PICKER_EOD } from '../DateTimePicker/utils';

/** Wall-clock EOD used when resolving lookbacks that depend on user EOD settings. */
export interface LookbackCustomEOD {
  hours: number;
  minutes: number;
  /** Optional IANA timezone. Absent/undefined = Local Time (browser). */
  timezone?: string;
}

export function resolveEodTimezone(customEOD: LookbackCustomEOD): string {
  return customEOD.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Given any instant, find what calendar day that instant falls on in a specific timezone, then return the UTC timestamp for EOD
 */
export function zonedEodOnCalendarDay(instant: Date, customEOD: LookbackCustomEOD = DEFAULT_DATE_PICKER_EOD): Date {
  const timezone = resolveEodTimezone(customEOD);
  const zoned = utcToZonedTime(instant, timezone);
  const wallClock = new Date(
    zoned.getFullYear(),
    zoned.getMonth(),
    zoned.getDate(),
    customEOD.hours,
    customEOD.minutes,
    0,
    0
  );
  return zonedTimeToUtc(wallClock, timezone);
}

/** EOD on the previous calendar day relative to `instant` in the user's EOD timezone. */
export function getPreviousCalendarDayEOD(
  instant: Date = new Date(),
  customEOD: LookbackCustomEOD = DEFAULT_DATE_PICKER_EOD
): Date {
  const timezone = resolveEodTimezone(customEOD);
  const zonedInstant = utcToZonedTime(instant, timezone);
  const yesterdayWallClock = subDays(
    new Date(zonedInstant.getFullYear(), zonedInstant.getMonth(), zonedInstant.getDate(), 12, 0, 0, 0),
    1
  );
  return zonedEodOnCalendarDay(zonedTimeToUtc(yesterdayWallClock, timezone), customEOD);
}

/** EOD on the next calendar day relative to `instant` in the user's EOD timezone. */
export function getNextCalendarDayEOD(
  instant: Date = new Date(),
  customEOD: LookbackCustomEOD = DEFAULT_DATE_PICKER_EOD
): Date {
  const timezone = resolveEodTimezone(customEOD);
  const zonedInstant = utcToZonedTime(instant, timezone);
  const tomorrowWallClock = addDays(
    new Date(zonedInstant.getFullYear(), zonedInstant.getMonth(), zonedInstant.getDate(), 12, 0, 0, 0),
    1
  );
  return zonedEodOnCalendarDay(zonedTimeToUtc(tomorrowWallClock, timezone), customEOD);
}

/**
 * Returns the most recent EOD instant relative to `now`:
 * - before today's EOD → yesterday's EOD (T-1)
 * - at or after today's EOD → today's EOD (T-0)
 *
 * Uses `customEOD.timezone` when set; otherwise the browser local timezone.
 */
export function getMostRecentEOD(now: Date = new Date(), customEOD: LookbackCustomEOD = DEFAULT_DATE_PICKER_EOD): Date {
  const todayEod = zonedEodOnCalendarDay(now, customEOD);

  if (now.getTime() < todayEod.getTime()) {
    return getPreviousCalendarDayEOD(now, customEOD);
  }

  return todayEod;
}

/**
 * EOD on the calendar day of `instant` in the EOD timezone.
 * If that instant is already in the past relative to `now`, returns the next calendar day's EOD.
 */
export function getUpcomingEOD(
  instant: Date = new Date(),
  customEOD: LookbackCustomEOD = DEFAULT_DATE_PICKER_EOD,
  now: Date = new Date()
): Date {
  const eod = zonedEodOnCalendarDay(instant, customEOD);
  if (eod.getTime() < now.getTime()) {
    return getNextCalendarDayEOD(instant, customEOD);
  }
  return eod;
}
